import { PICKER_PAGE_SIZE, type SearchHit } from '@palaccount/contracts';
import { SearchX } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router';

import { isSearchable, useGlobalSearch } from './api';
import { PageRow, ResultRow } from './global-search';
import { usePageMatches } from './nav-index';
import { GROUP_LABEL_KEYS, presentGroups, routeFor, totalAcross } from './result-routes';

/**
 * §4.3.2: *"**See all N results** | Opens a FULL RESULTS SCREEN; shown only
 * when more than 5 results exist in any group."*
 *
 * The same query, the same groups, the same rows — asked with a bigger
 * `perGroup`, which is the only difference between a panel and this screen and
 * is exactly how `PICKER_PAGE_SIZE` is described in `pagination.ts`: *"Twenty
 * rows is a panel, not a page."* Here twenty rows per group IS the page,
 * because the screen scrolls and the panel does not.
 *
 * The query lives in the URL rather than in component state, so the screen is
 * linkable, survives a refresh, and lands on the same rows when somebody sends
 * it to a colleague — the ordinary property of a results page, and the reason
 * `See all` navigates rather than expanding the panel in place.
 *
 * GEN-12a applies here unchanged and needs no code: the server fences the same
 * arms for the same caller, so a group this screen never receives is a group
 * it never draws. The PAGES group (G-05) is the one the browser answers, from
 * the same index and the same permission question as the panel, and here it
 * is drawn whole — the screen is where the panel's truncation is undone.
 */
export function SearchPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const q = params.get('q') ?? '';

  const { data, isFetching } = useGlobalSearch(q, PICKER_PAGE_SIZE);
  const pages = usePageMatches(isSearchable(q) ? q : '');
  const groups = presentGroups(data);
  const total = totalAcross(data) + pages.length;

  const open = (hit: SearchHit) => {
    void navigate(routeFor(hit));
  };

  const pagesSection =
    pages.length === 0 ? null : (
      <section>
        <h3 className="flex items-center justify-between px-3 pb-1 text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">
          <span>{t('search.groupPages')}</span>
          <span className="tabular-nums">{pages.length}</span>
        </h3>
        <ul className="divide-y divide-border rounded-md border border-border">
          {pages.map((page) => (
            <PageRow
              key={page.to}
              page={page}
              id={`result-page-${page.to}`}
              active={false}
              onHover={() => undefined}
              onPick={() => void navigate(page.to)}
            />
          ))}
        </ul>
      </section>
    );

  return (
    <div className="mx-auto w-full max-w-4xl p-6">
      <h2 className="text-lg font-semibold">
        {t('search.resultsFor')} <bdi className="font-bold">{q.trim()}</bdi>
      </h2>

      {!isSearchable(q) ? (
        <p className="mt-4 text-sm text-muted-foreground">{t('search.typeMore')}</p>
      ) : isFetching && data === undefined ? (
        <div className="mt-6 space-y-6">
          {pagesSection}
          <div className="space-y-2" aria-label={t('search.searching')}>
            {[0, 1, 2, 3, 4].map((row) => (
              <div key={row} className="h-12 animate-pulse rounded-md bg-muted" />
            ))}
          </div>
        </div>
      ) : total === 0 ? (
        <div className="mt-16 flex flex-col items-center gap-3 text-center text-muted-foreground">
          {/* The same sentence and the same absence of a button as the panel:
              one empty state, drawn twice, so the two surfaces cannot say
              different things about the same nothing. */}
          <SearchX className="size-8" aria-hidden />
          <p className="text-sm">
            {t('search.nothingMatches')} <bdi className="font-medium">{q.trim()}</bdi>
          </p>
        </div>
      ) : (
        <div className="mt-6 space-y-6">
          {pagesSection}
          {groups.map((group) => (
            <section key={group.key}>
              <h3 className="flex items-center justify-between px-3 pb-1 text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">
                <span>{t(GROUP_LABEL_KEYS[group.key])}</span>
                <span className="tabular-nums">{group.total}</span>
              </h3>
              {/* A real listbox per group: this screen has no single cursor
                  walking every row (that is the panel's job, GEN-12c), so
                  each group is its own list and the rows are reached by Tab
                  and the browser's own find. */}
              <ul className="divide-y divide-border rounded-md border border-border">
                {group.rows.map((hit) => (
                  <ResultRow
                    key={`${hit.type}-${hit.id}`}
                    hit={hit}
                    id={`result-${hit.type}-${hit.id}`}
                    active={false}
                    onHover={() => undefined}
                    onPick={() => open(hit)}
                  />
                ))}
              </ul>
              {group.total > group.rows.length ? (
                <p className="px-3 pt-2 text-xs text-muted-foreground">
                  {t('search.showingOf', { shown: group.rows.length, total: group.total })}
                </p>
              ) : null}
            </section>
          ))}
        </div>
      )}
    </div>
  );
}
